import { useState, useCallback } from "react";
import { apiService } from "../services/apiClient";
import { apiUrl } from "../services/apiUrl";

export const useToolRoleAdmin = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch the static catalog of assignable pages
  const fetchPagesCatalog = useCallback(async () => {
    try {
      const response = await apiService.get(apiUrl.toolRolePages());
      return response.data?.data ?? [];
    } catch (err) {
      console.error("Error fetching pages catalog:", err.response?.data?.message || err.message);
      throw err;
    }
  }, []);

  // Fetch all tool roles
  const fetchToolRoles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get(apiUrl.toolRoles());
      return response.data?.data ?? [];
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error fetching tool roles:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create new tool role
  const createToolRole = useCallback(async (roleData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.post(apiUrl.createToolRole(), roleData);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error creating tool role:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update (rename) tool role
  const updateToolRole = useCallback(async (id, roleData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.put(apiUrl.updateToolRole(id), roleData);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error updating tool role:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete tool role
  const deleteToolRole = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.delete(apiUrl.deleteToolRole(id));
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error deleting tool role:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch mapped pages + feature toggles for a tool role
  const fetchMappedPages = useCallback(async (toolRoleId) => {
    try {
      const response = await apiService.get(apiUrl.toolRoleMappedPagesById(toolRoleId));
      return {
        pageKeys: response.data?.data ?? [],
        features: response.data?.features ?? {},
      };
    } catch (err) {
      console.error("Error fetching mapped pages:", err.response?.data?.message || err.message);
      return { pageKeys: [], features: {} };
    }
  }, []);

  // Save page + feature mapping for a tool role
  const savePageMapping = useCallback(async (toolRoleId, pageKeys, features = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.post(apiUrl.toolRoleMapPages(), { toolRoleId, pageKeys, features });
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error saving page mapping:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch the merged list of pages assigned to a user (union of their active tool roles)
  const fetchUserAssignedPages = useCallback(async (email) => {
    try {
      const response = await apiService.get(apiUrl.toolRoleUserPages(email));
      return response.data;
    } catch (err) {
      console.error("Error fetching user assigned pages:", err.response?.data?.message || err.message);
      throw err;
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return {
    loading,
    error,
    fetchPagesCatalog,
    fetchToolRoles,
    createToolRole,
    updateToolRole,
    deleteToolRole,
    fetchMappedPages,
    savePageMapping,
    fetchUserAssignedPages,
    clearError,
  };
};

export default useToolRoleAdmin;
